import React, { useMemo, useState } from 'react';
import styles from './VendorDiffPreviewTable.module.css';

const FILTERS = [
  { value: 'changed', label: 'Changed only' },
  { value: 'all', label: 'All rows' },
  { value: 'errors', label: 'Errors only' },
];

const truncate = (value, limit = 60) =>
  value && value.length > limit ? `${value.slice(0, limit)}…` : value;

/**
 * One row's changes, rendered as `old → new` per field.
 *
 * An empty old value shows as a muted "(empty)" rather than a blank cell — a blank
 * there reads as a rendering bug rather than as "this field had no value".
 */
const ChangeList = ({ changes }) => (
  <ul className={styles.changeList}>
    {changes.map((change) => (
      <li key={change.field} className={styles.change}>
        <span className={styles.changeField}>{change.label}</span>
        <span className={styles.oldValue}>
          {change.old ? truncate(change.old) : <em className={styles.empty}>(empty)</em>}
        </span>
        <span className={styles.arrow} aria-label="changes to">
          →
        </span>
        <span className={styles.newValue} title={change.new}>
          {truncate(change.new)}
        </span>
      </li>
    ))}
  </ul>
);

const VendorDiffPreviewTable = ({ summary, results, onFixRow, validatingRow }) => {
  const [filter, setFilter] = useState(null);

  const errorCount = summary?.error_count || 0;
  // Default to "changed only" when the sheet is clean: on a 600-row round-trip
  // with five edits, showing everything buries the five.
  const activeFilter = filter || (errorCount ? 'all' : 'changed');

  const visibleRows = useMemo(() => {
    if (activeFilter === 'errors') return results.filter((r) => r.status === 'error');
    if (activeFilter === 'changed') {
      return results.filter((r) => r.changed || r.status === 'error');
    }
    return results;
  }, [results, activeFilter]);

  const hiddenCount = results.length - visibleRows.length;
  const fieldCounts = Object.entries(summary?.changed_field_counts || {});

  return (
    <div className={styles.wrapper}>
      <div className={styles.summaryRow}>
        <span className={`${styles.chip} ${styles.chipValid}`}>
          {summary?.valid_count || 0} with changes
        </span>
        <span className={`${styles.chip} ${styles.chipMuted}`}>
          {summary?.unchanged_count || 0} unchanged
        </span>
        {!!summary?.warning_count && (
          <span className={`${styles.chip} ${styles.chipWarning}`}>
            {summary.warning_count} warnings
          </span>
        )}
        <span className={`${styles.chip} ${styles.chipError}`}>{errorCount} errors</span>
        <span className={styles.total}>{summary?.total_rows || 0} rows total</span>
      </div>

      {!!fieldCounts.length && (
        <div className={styles.fieldCounts}>
          <span className={styles.fieldCountsLabel}>Fields changing:</span>
          {fieldCounts.map(([field, count]) => (
            <span key={field} className={styles.fieldCount}>
              {field} <strong>{count}</strong>
            </span>
          ))}
        </div>
      )}

      <div className={styles.filters}>
        {FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={
              activeFilter === option.value
                ? `${styles.filterBtn} ${styles.filterBtnActive}`
                : styles.filterBtn
            }
            onClick={() => setFilter(option.value)}
          >
            {option.label}
          </button>
        ))}
        {hiddenCount > 0 && <span className={styles.hiddenNote}>{hiddenCount} rows hidden</span>}
      </div>

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.numCol}>Row</th>
              <th className={styles.statusCol}>Status</th>
              <th className={styles.vendorCol}>Vendor</th>
              <th>Changes</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr
                key={row.row_number}
                className={row.status === 'error' ? styles.errorRow : undefined}
              >
                <td className={styles.numCol}>{row.row_number}</td>
                <td>
                  {row.status === 'error' && (
                    <span className={`${styles.badge} ${styles.badgeError}`}>Error</span>
                  )}
                  {row.status === 'unchanged' && (
                    <span className={`${styles.badge} ${styles.badgeMuted}`}>No change</span>
                  )}
                  {row.status === 'valid' && (
                    <span className={`${styles.badge} ${styles.badgeValid}`}>
                      {row.changes.length} change{row.changes.length === 1 ? '' : 's'}
                    </span>
                  )}
                </td>
                <td className={styles.vendorCol}>
                  {row.vendor_label ? (
                    <span className={styles.vendorLabel}>{row.vendor_label}</span>
                  ) : (
                    <span className={styles.empty}>—</span>
                  )}
                </td>
                <td>
                  {validatingRow === row.row_number && (
                    <span className={styles.validating}>Re-checking…</span>
                  )}
                  {!!Object.keys(row.errors || {}).length && (
                    <ul className={styles.errorList}>
                      {Object.entries(row.errors).map(([field, message]) => (
                        <li key={field}>
                          <strong>{field}:</strong> {message}
                          {onFixRow && (
                            <button
                              type="button"
                              className={styles.fixBtn}
                              onClick={() => onFixRow(row, field)}
                            >
                              Fix
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {!!row.warnings?.length && (
                    <ul className={styles.warningList}>
                      {row.warnings.map((warning) => (
                        <li key={warning}>⚠ {warning}</li>
                      ))}
                    </ul>
                  )}
                  {row.changes?.length ? (
                    <ChangeList changes={row.changes} />
                  ) : (
                    !Object.keys(row.errors || {}).length && (
                      <span className={styles.empty}>No changes</span>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!visibleRows.length && <p className={styles.emptyState}>Nothing to show for this filter.</p>}
    </div>
  );
};

export default VendorDiffPreviewTable;
